"use client";

import React, { useRef } from "react";
import { Upload, Paperclip, Trash2 } from "lucide-react";
import { Button, Input, cn } from "visuais";
import { IconFileDownloadFilled, IconTrashFilled } from "@tabler/icons-react";

interface LocalAnexo {
  item: string;
  nomeArquivo: string;
  observacao: string;
  isNew?: boolean;
}

interface UploadAreaProps {
  onFilesSelected: (files: FileList | File[]) => void;
  selectedFiles: File[];
  fileDescriptions: Map<string, string>;
  onFileDescriptionChange: (fileName: string, description: string) => void;
  onClearFiles: () => void;
  onRemoveFile?: (fileName: string) => void;
  disabled?: boolean;
  isUploading?: boolean;
  maxFiles?: number;
  currentFileCount?: number;
}

export function UploadArea({
  onFilesSelected,
  selectedFiles,
  fileDescriptions,
  onFileDescriptionChange,
  onClearFiles,
  onRemoveFile,
  disabled = false,
  isUploading = false,
  maxFiles = 20,
  currentFileCount = 0,
}: UploadAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const canAddMoreFiles = currentFileCount + selectedFiles.length < maxFiles;
  const isDropDisabled = disabled || isUploading || !canAddMoreFiles;

  return (
    <div className="space-y-3">
      {/* Área de Drop */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!isDropDisabled) {
            e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (!isDropDisabled) {
            onFilesSelected(e.dataTransfer.files);
          }
        }}
        onClick={() => !isDropDisabled && inputRef.current?.click()}
        className={cn(
          "w-full h-24 rounded-lg border-2 border-dashed p-3 flex flex-col items-center justify-center transition-colors",
          isDropDisabled
            ? "border-muted-foreground/30 cursor-not-allowed opacity-50"
            : "border-primary/30 hover:bg-primary/5 hover:border-primary/50 cursor-pointer"
        )}
      >
        <Upload
          className={cn(
            "w-5 h-5 mb-1",
            isDropDisabled ? "text-muted-foreground" : "text-primary"
          )}
        />
        <p className="text-xs font-medium text-foreground/80 text-center">
          {disabled
            ? "Sem REC válido"
            : isUploading
            ? "Enviando arquivos..."
            : !canAddMoreFiles
            ? `Limite de ${maxFiles} arquivos atingido`
            : "Arraste arquivos ou clique"}
        </p>
        {canAddMoreFiles && !isUploading && (
          <p className="text-[10px] text-muted-foreground/60 mt-1">
            {currentFileCount + selectedFiles.length}/{maxFiles} arquivos
          </p>
        )}
        <input
          type="file"
          multiple
          hidden
          ref={inputRef}
          disabled={isDropDisabled}
          onChange={(e) => {
            if (e.target.files && !isDropDisabled) {
              onFilesSelected(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>
      {/* Controles */}
      {selectedFiles.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {selectedFiles.length} arquivo(s) selecionado(s)
            </span>
            {selectedFiles.length > 0 && (
              <span className="text-[10px] text-muted-foreground/60">
                (
                {(
                  selectedFiles.reduce((total, file) => total + file.size, 0) /
                  1024 /
                  1024
                ).toFixed(1)}{" "}
                MB)
              </span>
            )}
          </div>
          <Button
            size="icon"
            variant="destructive"
            onClick={onClearFiles}
            disabled={isUploading}
            className="size-8 text-xs group"
          >
            <IconTrashFilled className="size-3 group-hover:size-4 transition-all duration-300" />
          </Button>
        </div>
      )}

      {/* Lista de arquivos selecionados */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-2 p-2 bg-muted/30 rounded-lg shadow-sm border"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-xs font-medium truncate"
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground/60 whitespace-nowrap">
                      ({(file.size / 1024 / 1024).toFixed(1)} MB)
                    </span>
                  </div>
                  <Input
                    placeholder="Descrição do arquivo..."
                    value={fileDescriptions.get(file.name) || ""}
                    onChange={(e) =>
                      onFileDescriptionChange(file.name, e.target.value)
                    }
                    className="h-6 text-[7pt] w-full"
                    disabled={isUploading}
                  />
                </div>
                {onRemoveFile && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onRemoveFile(file.name)}
                    disabled={isUploading}
                    className="size-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <IconTrashFilled className="size-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
